/**
 * Solana Staking Module implementation.
 *
 * @remarks
 * This module provides all Solana staking functionality including
 * validator management, stake account operations, and transaction building.
 */
import { Connection } from "@solana/web3.js";
import { SolanaRpcError, SolanaTransactionError } from "../../shared/error";
import { getLogger, maskSensitiveInUrl } from "../../shared/logger";
import { buildCreateNonceAccountTransaction as buildCreateNonceTx, buildDeactivateTransaction as buildDeactivateTx, buildSplitTransaction as buildSplitTx, buildStakeTransaction as buildStakeTx, buildWithdrawTransaction as buildWithdrawTx, createNonceAccount as createNonceOnChain, findStakeAccounts as findStakeAccountsTx, getStakeAccountInfo as getStakeAccountInfoTx, getStakeAccountsInfo as getStakeAccountsInfoTx, } from "./transactions";
import { validateCreateNonce, validateSplit, validateStake, validateWithdraw } from "./validators";
/**
 * Solana Staking Module class.
 *
 * @remarks
 * This class provides all functionality for Solana staking operations,
 * following the same patterns as BD-SDK modules for consistency.
 *
 * @example
 * ```typescript
 * const stakingModule = new SolanaStakingModule({
 *   rpcEndpoint: "https://api.mainnet-beta.solana.com",
 *   timeout: 30000,
 * });
 *
 * // Pick a validator vote account on the current cluster
 * const connection = stakingModule.getConnection();
 * const votes = await connection.getVoteAccounts();
 * const validatorVotePubkey = votes.current[0]?.votePubkey ?? votes.delinquent[0]?.votePubkey;
 * if (!validatorVotePubkey) throw new Error("No vote accounts available on cluster");
 *
 * // Build a stake transaction (unsigned, base64 returned)
 * const stakeResult = await stakingModule.buildStakeTransaction({
 *   payerPubkey: "...payer pubkey...",
 *   amount: 1_000_000_000, // 1 SOL in lamports
 *   validatorVotePubkey,
 * });
 * ```
 */
export class SolanaStakingModule {
    logger;
    connection;
    rpcHttpEndpoint;
    rpcWsEndpoint;
    timeout;
    // Transaction building is delegated to functions in ./transactions
    /**
     * Creates a new SolanaStakingModule instance.
     *
     * @param options - Configuration options for the module
     */
    constructor(options) {
        this.logger = options.logger || getLogger({ level: "info", label: "bd-sdk-staking-module" });
        this.rpcHttpEndpoint = options.rpcHttpEndpoint || "https://api.mainnet-beta.solana.com";
        this.rpcWsEndpoint = options.rpcWsEndpoint || "";
        this.timeout = options.timeout || 30000;
        this.logger.info("Initializing Solana Staking Module", {
            rpcHttpEndpoint: maskSensitiveInUrl(this.rpcHttpEndpoint),
            wssEndpoint: maskSensitiveInUrl(this.rpcWsEndpoint),
            timeout: this.timeout,
        });
        try {
            this.connection = new Connection(this.rpcHttpEndpoint, {
                wsEndpoint: this.rpcWsEndpoint,
                commitment: "finalized",
                confirmTransactionInitialTimeout: this.timeout,
            });
        }
        catch (error) {
            const message = `Failed to initialize Solana connection: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error(message);
            throw new SolanaRpcError(message, {
                originalError: error instanceof Error ? error : undefined,
            });
        }
        this.logger.info("Solana Staking Module initialized successfully");
    }
    /**
     * Exposes the underlying Solana `Connection` used by this module.
     */
    getConnection() {
        return this.connection;
    }
    /**
     * Performs a health check on the Solana connection.
     *
     * @returns Promise that resolves if the connection is healthy
     * @throws {SolanaRpcError} If the health check fails
     */
    async healthCheck() {
        this.logger.debug("Performing health check");
        try {
            const version = await this.connection.getVersion();
            this.logger.debug("Health check successful", { solanaVersion: version });
        }
        catch (error) {
            const message = `Health check failed: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error(message);
            throw new SolanaRpcError(message, {
                originalError: error instanceof Error ? error : undefined,
            });
        }
    }
    /**
     * Gets information about a specific stake account.
     *
     * @param stakeAccountPubkey - Public key of the stake account
     * @returns Promise that resolves to stake account information
     * @throws {SolanaStakingError} If stake account information cannot be retrieved
     *
     * @example
     * ```typescript
     * import { LAMPORTS_PER_SOL } from "@solana/web3.js";
     *
     * const stakeInfo = await stakingModule.getStakeAccountInfo("stake-account-pubkey");
     * console.log(`Stake balance: ${stakeInfo.balance / LAMPORTS_PER_SOL} SOL`);
     * ```
     */
    async getStakeAccountInfo(stakeAccountPubkey, options) {
        return await getStakeAccountInfoTx({ connection: this.connection, logger: this.logger }, stakeAccountPubkey, options);
    }
    async findStakeAccounts(ownerPubkey, filter) {
        return await findStakeAccountsTx({ connection: this.connection, logger: this.logger }, { ownerPubkey, ...(filter ? { filter } : {}) });
    }
    /**
     * Get stake account info for multiple accounts discovered by owner or provided explicitly.
     */
    async getStakeAccountsInfo(request) {
        return await getStakeAccountsInfoTx({ connection: this.connection, logger: this.logger }, request);
    }
    /**
     * Builds a transaction to create and delegate a stake account.
     *
     * @param request - Stake transaction request parameters
     * @returns Promise that resolves to the built transaction
     * @throws {SolanaTransactionError} If the transaction cannot be built
     *
     * @example
     * ```typescript
     * const stakeResult = await stakingModule.buildStakeTransaction({
     *   payerPubkey: "payer-pubkey",
     *   amount: 1000000000, // 1 SOL in lamports
     *   validatorVotePubkey: "validator-vote-pubkey"
     * });
     * ```
     */
    async buildStakeTransaction(request) {
        try {
            const built = await buildStakeTx({ connection: this.connection, logger: this.logger }, request);
            const validation = validateStake(built, request);
            if (validation.warnings.length > 0) {
                this.logger.warn("Stake tx validation warnings", { warnings: validation.warnings });
            }
            if (!validation.ok) {
                throw new SolanaTransactionError(`Stake transaction validation failed: ${validation.errors.join("; ")}`);
            }
            return built;
        }
        catch (error) {
            const message = `Failed to build stake transaction: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error(message);
            throw new SolanaTransactionError(message, {
                originalError: error instanceof Error ? error : undefined,
            });
        }
    }
    /**
     * Builds a transaction to create a durable nonce account with payer as authority.
     */
    async buildCreateNonceAccountTransaction(request) {
        try {
            const result = await buildCreateNonceTx({ connection: this.connection, logger: this.logger }, request);
            const validation = validateCreateNonce(result, request);
            if (validation.warnings.length > 0) {
                this.logger.warn("Create nonce tx validation warnings", { warnings: validation.warnings });
            }
            if (!validation.ok) {
                throw new SolanaTransactionError(`Create nonce transaction validation failed: ${validation.errors.join("; ")}`);
            }
            return {
                transaction: result.transaction,
                unsignedTransaction: result.unsignedTransaction,
                nonceAccountPubkey: result.nonceAccountPubkey,
                authorityPubkey: result.authorityPubkey,
                instructionCount: result.instructionCount,
                estimatedFee: result.estimatedFee,
                metadata: result.metadata,
            };
        }
        catch (error) {
            const message = `Failed to build create nonce account transaction: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error(message);
            throw new SolanaTransactionError(message, {
                originalError: error instanceof Error ? error : undefined,
            });
        }
    }
    /**
     * Creates a durable nonce account on-chain and returns authority and nonce account pubkeys.
     */
    async createNonceAccount(request) {
        try {
            return await createNonceOnChain({ connection: this.connection, logger: this.logger }, request);
        }
        catch (error) {
            const message = `Failed to create nonce account: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error(message);
            throw new SolanaTransactionError(message, {
                originalError: error instanceof Error ? error : undefined,
            });
        }
    }
    /**
     * Builds a transaction to deactivate a stake account.
     *
     * @param request - Deactivate transaction request parameters
     * @returns Promise that resolves to the built transaction
     * @throws {SolanaTransactionError} If the transaction cannot be built
     *
     * @remarks
     * Deactivating a stake account begins the process of undelegating from a validator.
     * The stake account will become inactive at the start of the next epoch after the
     * transaction is processed. Once deactivated, the stake can be withdrawn.
     *
     * Note: This method is currently a stub and not yet implemented. Calling it will
     * throw a `SolanaTransactionError` with a "not yet implemented" message.
     *
     * @example
     * ```typescript
     * const deactivateResult = await stakingModule.buildDeactivateTransaction({
     *   stakeAccountPubkey: "stake-account-pubkey",
     *   stakeAuthorityPubkey: "stake-authority-pubkey"
     * });
     * ```
     */
    async buildDeactivateTransaction(request) {
        try {
            return await buildDeactivateTx({ connection: this.connection, logger: this.logger }, request);
        }
        catch (error) {
            const message = `Failed to build deactivate transaction: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error(message);
            throw new SolanaTransactionError(message, {
                originalError: error instanceof Error ? error : undefined,
            });
        }
    }
    /**
     * Builds a transaction to withdraw SOL from a deactivated stake account.
     *
     * @param request - Withdraw transaction request parameters
     * @returns Promise that resolves to the built transaction
     * @throws {SolanaTransactionError} If the transaction cannot be built
     *
     * @remarks
     * Withdrawing from a stake account removes SOL and transfers it to a destination account.
     * The stake account must be deactivated before withdrawal is possible. If withdrawing
     * the full amount, the stake account will be closed and all remaining lamports transferred.
     *
     * Note: This method is currently a stub and not yet implemented. Calling it will
     * throw a `SolanaTransactionError` with a "not yet implemented" message.
     *
     * @example
     * ```typescript
     * const withdrawResult = await stakingModule.buildWithdrawTransaction({
     *   stakeAccountPubkey: "stake-account-pubkey",
     *   withdrawerPubkey: "withdrawer-pubkey",
     *   destinationPubkey: "destination-pubkey",
     *   amount: 1000000000 // 1 SOL in lamports (optional)
     * });
     * ```
     */
    async buildWithdrawTransaction(request) {
        try {
            const built = await buildWithdrawTx({ connection: this.connection, logger: this.logger }, request);
            const validation = validateWithdraw(built, request);
            if (validation.warnings.length > 0) {
                this.logger.warn("Withdraw tx validation warnings", { warnings: validation.warnings });
            }
            if (!validation.ok) {
                throw new SolanaTransactionError(`Withdraw transaction validation failed: ${validation.errors.join("; ")}`);
            }
            return built;
        }
        catch (error) {
            const message = `Failed to build withdraw transaction: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error(message);
            throw new SolanaTransactionError(message, {
                originalError: error instanceof Error ? error : undefined,
            });
        }
    }
    /**
     * Builds a transaction to split lamports from a stake account into a new stake account.
     */
    async buildSplitTransaction(request) {
        try {
            const built = await buildSplitTx({ connection: this.connection, logger: this.logger }, request);
            const validation = validateSplit(built, request);
            if (validation.warnings.length > 0) {
                this.logger.warn("Split tx validation warnings", { warnings: validation.warnings });
            }
            if (!validation.ok) {
                throw new SolanaTransactionError(`Split transaction validation failed: ${validation.errors.join("; ")}`);
            }
            return built;
        }
        catch (error) {
            const message = `Failed to build split transaction: ${error instanceof Error ? error.message : String(error)}`;
            this.logger.error(message);
            throw new SolanaTransactionError(message, {
                originalError: error instanceof Error ? error : undefined,
            });
        }
    }
}
//# sourceMappingURL=data:application/json;base64,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